import React, { useEffect, useState, useRef } from 'react';
import { Send, Shield, FileText, Info } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChatMessage } from '../../data/types';
import { simulateAIResponse, getInitialChatHistory } from '../../utils/aiSimulator';
import { FORM_TEMPLATES } from '../../data/mockData';
import clsx from 'clsx';
export function EmployeeChat() {
  const [messages, setMessages] = useState<ChatMessage[]>(getInitialChatHistory());
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);
  const handleSend = async () => {
    if (!inputValue.trim()) return;
    const userMsg: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMsg]);
    setInputValue('');
    setIsTyping(true);
    // Simulate AI processing
    const aiResponse = await simulateAIResponse(userMsg.content);
    setIsTyping(false);
    setMessages(prev => [...prev, aiResponse]);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };
  return <div className="h-screen flex flex-col bg-background relative overflow-hidden" data-id="element-153">
      {/* Background Ambient Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" data-id="element-154" />

      {/* Header */}
      <header className="flex-none p-6 flex items-center justify-center relative z-10" data-id="element-155">
        <div className="glass-panel px-6 py-2 rounded-full flex items-center space-x-3 animate-fade-in" data-id="element-156">
          <div className="relative" data-id="element-157">
            <Shield className="w-5 h-5 text-accent" data-id="element-158" />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-success rounded-full animate-pulse" data-id="element-159" />
          </div>
          <span className="text-sm font-medium text-white" data-id="element-160">
            Your voice is anonymous
          </span>
        </div>
      </header>

      {/* Chat Area */}
      <div className="flex-1 overflow-y-auto px-4 py-6 scrollbar-hide" data-id="element-161">
        <div className="max-w-2xl mx-auto space-y-6" data-id="element-162">
          {messages.map(msg => <motion.div key={msg.id} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className={clsx('flex flex-col max-w-[85%]', msg.role === 'user' ? 'ml-auto items-end' : 'mr-auto items-start')} data-id="element-163">
              <div className={clsx('p-4 rounded-2xl text-sm leading-relaxed shadow-lg', msg.role === 'user' ? 'bg-surfaceHighlight text-white rounded-br-none' : 'glass-card text-gray-200 rounded-bl-none border-l-2 border-l-accent')} data-id="element-164">
                {msg.content}
              </div>

              {/* Sources for Q&A */}
              {msg.sources && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="mt-2 ml-2 bg-surface/50 rounded-lg p-3 border border-white/5 text-xs w-full max-w-md" data-id="element-165">
                  <div className="flex items-center text-secondary mb-2" data-id="element-166">
                    <Info className="w-3 h-3 mr-1" data-id="element-167" />
                    <span className="uppercase tracking-wider font-semibold" data-id="element-168">
                      Sources
                    </span>
                  </div>
                  <ul className="space-y-1" data-id="element-169">
                    {msg.sources.map((source, idx) => <li key={idx} className="text-accent hover:underline cursor-pointer flex items-center" data-id="element-170">
                        <FileText className="w-3 h-3 mr-1.5" data-id="element-171" />
                        {source}
                      </li>)}
                  </ul>
                </motion.div>}

              <span className="text-[10px] text-secondary mt-1 px-1 opacity-50" data-id="element-172">
                {msg.timestamp.toLocaleTimeString([], {
              hour: '2-digit',
              minute: '2-digit'
            })}
              </span>
            </motion.div>)}

          {isTyping && <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex items-center space-x-2 p-4 glass-card rounded-2xl rounded-bl-none w-24" data-id="element-173">
              <div className="w-2 h-2 bg-accent/50 rounded-full animate-bounce" style={{
            animationDelay: '0ms'
          }} data-id="element-174" />
              <div className="w-2 h-2 bg-accent/50 rounded-full animate-bounce" style={{
            animationDelay: '150ms'
          }} data-id="element-175" />
              <div className="w-2 h-2 bg-accent/50 rounded-full animate-bounce" style={{
            animationDelay: '300ms'
          }} data-id="element-176" />
            </motion.div>}
          <div ref={messagesEndRef} data-id="element-177" />
        </div>
      </div>

      {/* Input Area */}
      <div className="flex-none p-6 pb-8 relative z-10" data-id="element-178">
        <div className="max-w-2xl mx-auto" data-id="element-179">
          <div className="relative group" data-id="element-180">
            <div className="absolute -inset-0.5 bg-gradient-to-r from-accent/20 to-surfaceHighlight rounded-xl blur opacity-30 group-hover:opacity-50 transition duration-500" data-id="element-181" />
            <div className="relative flex items-end bg-surface rounded-xl border border-white/10 shadow-2xl" data-id="element-182">
              <textarea value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Share feedback or ask a question..." className="w-full bg-transparent text-white placeholder-secondary/50 p-4 min-h-[60px] max-h-[150px] resize-none focus:outline-none text-base" rows={1} data-id="element-183" />
              <button onClick={handleSend} disabled={!inputValue.trim() || isTyping} className="m-2 p-2 rounded-lg bg-accent text-background hover:bg-accentHover disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-184">
                <Send className="w-5 h-5" data-id="element-185" />
              </button>
            </div>
          </div>

          {/* Active Forms Quick Access */}
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-186">
            {FORM_TEMPLATES.map(form => <div key={form.id} className="glass-card p-3 rounded-lg flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors group" data-id="element-187">
                <div className="flex items-center space-x-3 min-w-0" data-id="element-188">
                  <div className={clsx('w-8 h-8 rounded-full flex items-center justify-center shrink-0', form.status === 'submitted' ? 'bg-success/20 text-success' : 'bg-accent/20 text-accent')} data-id="element-189">
                    <FileText className="w-4 h-4" data-id="element-190" />
                  </div>
                  <div className="min-w-0" data-id="element-191">
                    <p className="text-xs font-medium text-white truncate" data-id="element-192">
                      {form.title}
                    </p>
                    <p className="text-[10px] text-secondary" data-id="element-193">
                      {form.status === 'submitted' ? 'Submitted' : `Due ${form.dueDate}`}
                    </p>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}